<template>
  <button
    :class="buttonClasses"
    :disabled="disabled"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @mousedown="handleMouseDown"
    @mouseup="handleMouseUp"
  >
    <StandardButtonIcons 
      v-if="computedLeftIcon"
      :name="computedLeftIcon"
      :buttonType="type"
      :buttonVariant="variant"
      :disabled="disabled"
    />
    <slot>{{ label }}</slot>
    <StandardButtonIcons 
      v-if="computedRightIcon"
      :name="computedRightIcon"
      :buttonType="type"
      :buttonVariant="variant"
      :disabled="disabled"
    />
  </button>
</template>

<script setup>
import { ref, computed } from 'vue';
import StandardButtonIcons from './StandardButtonIcons.vue';

/**
 * StandardButton Component Props
 * A comprehensive Vue 3 button component with multiple types, variants, and sizes
 */

const props = defineProps({
  type: {
    type: String,
    default: 'magenta',
    validator: (value) => ['magenta', 'red', 'green', 'terminated'].includes(value)
  },
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => [
      'primary', 
      'secondary', 
      'tertiary', 
      'outline', 
      'text-secondary', 
      'text-primary'
    ].includes(value)
  },
  size: {
    type: String,
    default: 'normal',
    validator: (value) => [
      'xs', 
      'small', 
      'normal', 
      'medium', 
      'large', 
      'xl', 
      'full-width'
    ].includes(value)
  },
  label: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  leftIcon: {
    type: String,
    default: null
  },
  rightIcon: {
    type: String,
    default: null
  },
  loaderLeft: {
    type: Boolean,
    default: false
  },
  loaderRight: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click']);

// Internal state for hover and active interactions
const isHovered = ref(false);
const isActive = ref(false);

const computedLeftIcon = computed(() => {
  if (props.loaderLeft) {
    return 'loader';
  }
  return props.leftIcon;
});
const computedRightIcon = computed(() => {
  if (props.loaderRight) {
    return 'loader';
  }
  return props.rightIcon;
});
const buttonClasses = computed(() => {
  const classes = ['standard-button'];
  
  // Add type class
  classes.push(`standard-button--${props.type}`);
  
  // Add variant class (terminated type only supports primary variant)
  const variant = props.type === 'terminated' ? 'primary' : props.variant;
  classes.push(`standard-button--${variant}`);
  
  // Add size class
  classes.push(`standard-button--${props.size}`);
  
  // Add state classes based on current interaction state
  if (props.disabled) {
    classes.push('standard-button--disabled');
  } else if (isHovered.value) {
    classes.push('standard-button--hover');
  } else {
    classes.push('standard-button--default');
  }
  
  return classes;
});
const handleClick = (event) => {
  if (!props.disabled) {
    emit('click', event);
  }
};
const handleMouseEnter = () => {
  if (!props.disabled) {
    isHovered.value = true;
  }
};
const handleMouseLeave = () => {
  isHovered.value = false;
  isActive.value = false;
};
const handleMouseDown = () => {
  if (!props.disabled) {
    isActive.value = true;
  }
};
const handleMouseUp = () => {
  isActive.value = false;
};
</script>

<style scoped>
@import './standardButton.css';
</style>
